import { handleApostropheCode } from '../../../pages/exercises/services/Misc';
import {
	exerciseTypeCodesNext,
	IS_MOBILE_DEVICE,
	PUNCTUATION_CHARACTERS,
	SOURCE_TYPES,
	TEST_TYPES,
} from '../../../services/Constants';

export const handleAnimation = (options) => {
	const { exerciseRef } = options;
	if (exerciseRef && exerciseRef.current)
		exerciseRef.current.setAttribute('class', 'exercise fade-right');
};

export const handleBottomBarAnimation = (options) => {
	const { type, isCorrect, successRespRef, errorRespRef, displaySummary } = options;
	const isMultipleChoiceType = () => exerciseTypeCodesNext.MULTIPLE_CHOICE === type;
	const isInYourMindType = () => exerciseTypeCodesNext.IN_YOUR_MIND === type;
	const statusRefs = {
		success: successRespRef,
		error: errorRespRef,
	};
	const status = isCorrect ? 'success' : 'error';
	if (isInYourMindType()) return;
	if (!isMultipleChoiceType()) {
		statusRefs[status] &&
			statusRefs[status].current?.setAttribute(
				'class',
				`resp resp-${status} space-between fadeInUp ${displaySummary && 'answer-summary'}`,
			);
	} else {
		statusRefs[status] &&
			statusRefs[status].current?.setAttribute('class', `resp resp-bottom-bar-${status}`);
	}
};

export const getMultipleAnswersCount = (currentItem) => {
	return currentItem.answers.filter((item) => item.is_correct).length;
};

export const preparedData = (preparedMultipleAnswers) => {
	return Object.entries(preparedMultipleAnswers).reduce((acc, [k, v]) => {
		acc.push(v);
		return acc;
	}, []);
};

export const removeValue = (key, preparedMultipleAnswers) => {
	return Object.entries(preparedMultipleAnswers).reduce((acc, [k, v]) => {
		if (k !== String(key)) {
			acc[k] = v;
		}
		return acc;
	}, {});
};

export const setFocusOnFieldForMultipleTypes = (blankSectionRef) => {
	const firstInput =
		blankSectionRef?.current?.getElementsByTagName('input')[0] ||
		blankSectionRef?.current?.getElementsByTagName('textarea')[0];

	if (firstInput) {
		const inputWrapBottom = firstInput.getBoundingClientRect()?.bottom;
		if (!IS_MOBILE_DEVICE && window.innerHeight > inputWrapBottom) {
			firstInput.focus();
		}
	}
};

export const checkDoWeHaveIncorrectAnswer = (currentItem, preparedMultipleAnswers, isMCType) => {
	let hasAtLeastOneIncorrect = false;
	for (const [i, value] of Object.entries(preparedMultipleAnswers)) {
		const answerItem = isMCType
			? currentItem.answers[i].is_correct && currentItem.answers[i].answer_display
			: currentItem.question_display[i][1];
		if (value.trim() !== (typeof answerItem !== 'string' ? answerItem : answerItem.trim()))
			hasAtLeastOneIncorrect = true;
	}

	return { isCorrect: !hasAtLeastOneIncorrect, typoError: false };
};

export const checkDoWeHaveIncorrectAnswers = (preparedBlanksMultipleAnswers) => {
	let hasAtLeastOneIncorrect = false;
	let isTypo = true;
	for (const [i, v] of Object.entries(preparedBlanksMultipleAnswers)) {
		if (!v.isCorrect) hasAtLeastOneIncorrect = true;
		if (
			v.alternativeAnswers.filter(
				(answer) => answer?.trim().length === v.value?.trim().length,
			).length === 0
		) {
			isTypo = false;
		}
	}

	return { isCorrect: !hasAtLeastOneIncorrect, typoError: isTypo };
};

export const isDictateAnswerCorrect = (currentItem, userAnswer, answerItem) => {
	const answers = currentItem.answers.reduce((acc, item) => {
		item.answer_variations.forEach((el) => {
			acc.push(el.answer_text);
		});
		acc.push(item.answer_display);
		return acc;
	}, []);

	const isTypoError = answers.filter(
		(answer) => answer.trim().length === userAnswer.trim().length,
	).length;

	const isCorrect = getIsCorrect(
		userAnswer,
		'',
		{ case_sensitive: false, punctuation_sensitive: false },
		answers,
	);

	return { isCorrect, typoError: isTypoError };
};

const checkPunctuation_ = (word) => {
	const wordArray = Array.isArray(word) ? word : Array.from(word);

	return wordArray.filter((el) => PUNCTUATION_CHARACTERS.indexOf(el) === -1).join('');
};

const checkCapitals_ = (word) => {
	return word.toLowerCase();
};

export const getIsCorrect = (
	value,
	answerW,
	{ case_sensitive, punctuation_sensitive },
	extraAnswerVariants,
) => {
	const splittedAnswers = extraAnswerVariants.map((item) => handleApostropheCode(item));
	let finalAnswers = [...splittedAnswers];
	let proposedWord_ = value && handleApostropheCode(value);
	const splittedProposedWord = proposedWord_ && proposedWord_.split('');

	if (!punctuation_sensitive && value) {
		proposedWord_ = checkPunctuation_(splittedProposedWord);
		finalAnswers = finalAnswers.map((answer) => checkPunctuation_(answer.split('')));
	}

	if (!case_sensitive && proposedWord_) {
		proposedWord_ = checkCapitals_(proposedWord_);
		finalAnswers = finalAnswers.map((answer) => checkCapitals_(answer));
	}

	return finalAnswers.filter((item) => item.trim() === proposedWord_.trim()).length;
};

export const getExtraAnswersOptions = (answersArr, answerId) => {
	return answersArr.reduce((acc, answer) => {
		if (answer.id === answerId) {
			acc.push(answer.answer_display);
			if (answer.answer_variations && answer.answer_variations.length) {
				answer.answer_variations.forEach((item) => acc.push(item.answer_text));
			}
		}
		return acc;
	}, []);
};

export const getMaxItemLength = (items) => {
	return items.reduce((acc, item) => {
		if (acc < (item && item.length)) {
			acc = item.length;
		}
		return acc;
	}, 0);
};

export const getCorrectAnswer = (answers) =>
	answers.reduce((acc, item) => {
		if (item.is_correct) acc = item;
		return acc;
	}, {});

export const getCorrectAnswers = (answers) =>
	answers.reduce((acc, item) => {
		if (item.is_correct) acc.push(item.answer_display);
		return acc;
	}, []);

export const getExerciseType = (type) => {
	switch (type) {
		case 'topic':
			return TEST_TYPES.REGULAR;
		case 'user_quiz':
			return TEST_TYPES.QUIZ;
		case 'full_exam':
			return TEST_TYPES.EXAM;
		case 'exam_practice_set':
			return TEST_TYPES.SET;
		default:
			return TEST_TYPES.DIAGNOSTIC;
	}
};

export const getExerciseSource = (exerciseType) => {
	switch (exerciseType) {
		case TEST_TYPES.REGULAR:
			return SOURCE_TYPES.TOPIC_EXERCISE;
		case TEST_TYPES.QUIZ:
			return SOURCE_TYPES.QUIZ_TEST;
		case TEST_TYPES.EXAM:
			return SOURCE_TYPES.EXAM_EXERCISE;
		case TEST_TYPES.SET:
			return SOURCE_TYPES.SET_EXERCISE;
		default:
			return SOURCE_TYPES.DIAGNOSTIC_TEST;
	}
};

export const getExerciseHelmetTitle = (exerciseType) => {
	switch (exerciseType) {
		case TEST_TYPES.REGULAR:
			return 'regular_test';
		case TEST_TYPES.QUIZ:
			return 'quiz_test';
		case TEST_TYPES.EXAM:
			return 'full_exam_test';
		case TEST_TYPES.SET:
			return 'exam_practice_set_test';
		default:
			return 'diagnostic_test';
	}
};

export const getExerciseTitle = (exerciseType, topicTitle, quizTitle, chapterTitle, examTitle) => {
	switch (exerciseType) {
		case TEST_TYPES.REGULAR:
			return { topicName: topicTitle };
		case TEST_TYPES.QUIZ:
			return { quizTitle };
		case TEST_TYPES.EXAM:
		case TEST_TYPES.SET:
			return { examTitle };
		default:
			return { chapterTitle };
	}
};

export const updatePreparedAnswers = (preparedAnswers, status) =>
	Object.entries(preparedAnswers).reduce((acc, [k, v]) => {
		acc[k] = {
			...v,
			isTypo: status,
		};
		return acc;
	}, {});

export const generateTypoCheckerObject = (preparedAnswers) =>
	Object.entries(preparedAnswers).reduce((acc, [k, v]) => {
		const alternative = v.alternativeAnswers.map((item) => item.trim());
		acc.push({ input_string: v.value.trim(), correct_strings: alternative });
		return acc;
	}, []);

export const generateTypoCheckerObjectSingleAnswer = (currentItem, userSingleAnswer) =>
	currentItem.answers.reduce((acc, item) => {
		const correctStrings = item.answer_variations.length
			? [
					...item.answer_variations.map((el) => el.answer_text.trim()),
					item.answer_display.trim(),
			  ]
			: [item.answer_display.trim()];
		acc.push({ input_string: userSingleAnswer.trim(), correct_strings: correctStrings });
		return acc;
	}, []);

export const getScoreData = (questions) =>
	questions.reduce(
		(acc, item) => {
			const { exercise_answers, type: questionType } = item;
			const isInYourMindMultiple =
				questionType === exerciseTypeCodesNext.IN_YOUR_MIND &&
				exercise_answers &&
				exercise_answers.length > 1;

			if (exercise_answers && exercise_answers.length) {
				const notSkippedQuestions = exercise_answers.filter((answer) => !answer.is_skipped);

				const correctAnswersCount =
					!!notSkippedQuestions.length &&
					notSkippedQuestions.filter(
						(notSkippedQuestion) => notSkippedQuestion.is_correct,
					).length;

				const incorrectAnswersCount =
					!!notSkippedQuestions.length &&
					notSkippedQuestions.filter(
						(notSkippedQuestion) => !notSkippedQuestion.is_correct,
					).length;

				if (notSkippedQuestions.length && !isInYourMindMultiple) {
					incorrectAnswersCount ? (acc.incorrect += 1) : (acc.correct += 1);
				}
				if (isInYourMindMultiple) {
					acc.incorrect += incorrectAnswersCount;
					acc.correct += correctAnswersCount;
				}
			} else acc.wordsLeft.push(item);

			return acc;
		},
		{ correct: 0, incorrect: 0, wordsLeft: [] },
	);
